import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";
import React, { useState } from "react";
import { TimeTrackingSession } from "../../types";
import { minutesToTimeString } from "./EditInputs/utils";
import { Session } from "./Session";

interface SessionGroupProps {
  sessions: TimeTrackingSession[];
  projectId: string;
}

const STATIC_TIME_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid white",
  borderRadius: 4,
  width: 80,
  fontWeight: 500,
  fontSize: 16,
  color: "#333",
  background: "transparent",
  textAlign: "center",
};

export const SessionGroup: React.FC<SessionGroupProps> = ({
  sessions,
  projectId,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (sessions.length === 1) {
    return <Session session={sessions[0]} projectId={projectId} />;
  }

  const totalMinutes = sessions.reduce((sum, s) => sum + s.time, 0);
  const representative = sessions[0];

  const earliestStart = sessions.reduce(
    (min, s) => (s.start < min ? s.start : min),
    sessions[0].start,
  );
  const latestEnd = sessions.reduce(
    (max, s) => (s.end > max ? s.end : max),
    sessions[0].end,
  );

  const startFormatted = moment.utc(earliestStart).local().format("HH:mm");
  const endFormatted = moment.utc(latestEnd).local().format("HH:mm");
  const totalFormatted = minutesToTimeString(totalMinutes);

  return (
    <div>
      <div
        style={{
          padding: "8px",
          paddingLeft: "2px",
          paddingRight: "2px",
          width: "100%",
          marginTop: "8px",
          marginBottom: expanded ? 0 : "8px",
          fontWeight: 600,
          cursor: "pointer",
          backgroundColor: expanded ? "#f5f7ff" : "white",
          borderRadius: expanded ? "6px 6px 0 0" : undefined,
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 4px",
            gap: "4px",
          }}
        >
          <div
            style={{
              minWidth: 22,
              height: 22,
              borderRadius: 4,
              backgroundColor: "#137AE9",
              color: "white",
              fontSize: 11,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              padding: "0 5px",
              marginRight: 2,
            }}
            title={`${sessions.length} time entries (click to toggle)`}
          >
            {sessions.length}
          </div>

          <div
            style={{
              flex: 1,
              fontWeight: 500,
              fontSize: 16,
              color: "#333",
              paddingLeft: 8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "left",
            }}
          >
            {representative.description || "No description"}
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <div style={STATIC_TIME_STYLE}>{startFormatted}</div>
            <span style={{ fontSize: 20 }}>-</span>
            <div style={STATIC_TIME_STYLE}>{endFormatted}</div>
          </div>

          <div
            style={{
              borderRight: "dotted",
              borderWidth: "2px",
              borderColor: "grey",
              minHeight: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "51px",
            }}
          >
            <CalendarOutlined
              style={{
                fontSize: 24,
                color: "black",
              }}
            />
          </div>

          <div
            style={{
              fontWeight: 700,
              fontSize: "18px",
              borderRight: "dotted",
              borderWidth: "2px",
              borderColor: "grey",
              minHeight: "36px",
              display: "flex",
              alignItems: "center",
              marginRight: "16px",
              justifyContent: "center",
              width: "88px",
              paddingLeft: "4px",
              paddingRight: "4px",
              textAlign: "center",
            }}
          >
            {totalFormatted}
          </div>

          <div style={{ width: 40, flexShrink: 0 }} />
        </div>
      </div>

      {expanded && (
        <div
          style={{
            borderRadius: "0 0 6px 6px",
            marginBottom: 8,
            backgroundColor: "#fafcff",
          }}
        >
          {sessions.map((session) => (
            <Session key={session.id} session={session} projectId={projectId} />
          ))}
        </div>
      )}
    </div>
  );
};
